import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Select } from 'antd'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import uuid from 'uuid'
import { isArray, isEmpty } from 'lodash-es'
import { loadedInitSelector, optionsSelector, userSelector } from 'app-redux/selectors'
// import { getUsers } from 'utils/apis'
// import { filterOption } from 'utils/helper'
import { useTerm } from 'utils/hooks'
import { usePosUsers, useUsers } from 'utils/queries'
import useMemoizeCheckPermission from 'utils/hooks/useMemoizeCheckPermission'

const UserSuggestion = ({
  mode = 'single',
  innerRef,
  disabled,
  defaultUser,
  onSelect,
  rules,
  hideTagOnFirstLoading,
  isReadyToFetch = false,
  withPermission = true,
  hideLabel,
  excludeUsers,
  filterExcludeDefaultUser,
  isPos,
  label,
  normalize,
  tooltip,
  params,
  ...props
}) => {
  const containerId = useRef(`suggestion-user-${uuid()}`)
  const { formatMessage } = useIntl()
  const loadedInit = useSelector(loadedInitSelector)
  const options = useSelector(optionsSelector)
  const activeUser = useSelector(userSelector)
  const isOtherSalesPersonAccessPermission = useMemoizeCheckPermission({
    permission: 'other_sales_person_access',
  })
  const isOtherSalesPersonAccess = withPermission ? isOtherSalesPersonAccessPermission : true

  const [open, setOpen] = useState(false)
  const [enabledQuery, setEnabledQuery] = useState(false)

  useEffect(() => {
    if ((open || isReadyToFetch) && isOtherSalesPersonAccess && !enabledQuery) {
      setEnabledQuery(true)
    }
  }, [enabledQuery, isOtherSalesPersonAccess, isReadyToFetch, open])

  const fetcher = isPos ? usePosUsers : useUsers

  const { data, isLoading } = fetcher({
    payload: params,
    options: { enabled: enabledQuery },
  })
  const term = useTerm()

  const makeDefaultUser = useMemo(() => {
    return isEmpty(defaultUser) ? activeUser : defaultUser
  }, [activeUser, defaultUser])

  // React.useEffect(() => {
  //   if (defaultUser) {
  //     if (mode === 'multiple') {
  //       setUsers(defaultUser)
  //     } else {
  //       setUsers([defaultUser])
  //     }
  //   }
  // }, [defaultUser, mode])

  const formattedOptions = useMemo(() => {
    let filteredData = !isEmpty(makeDefaultUser)
      ? isArray(makeDefaultUser)
        ? makeDefaultUser
        : [makeDefaultUser]
      : []
    if ((!isOtherSalesPersonAccess || !data?.data) && !filterExcludeDefaultUser) {
      return filteredData
    }

    if (Array.isArray(data?.data)) {
      filteredData = data.data.filter(
        // token_website_id filter hanya yang udah join, token_website_id ada value berarti belum join
        // can_add_sales dipakai di sales untuk filter yang punya hak akses tambah invoice
        (user) => user.is_active && user.can_add_sales !== false && !user.token_website_id,
      )
    }

    if (excludeUsers) {
      filteredData = filteredData.filter((user) => !excludeUsers.includes(user.id))
    }

    return filteredData
  }, [data, excludeUsers, filterExcludeDefaultUser, isOtherSalesPersonAccess, makeDefaultUser])

  const selectedHandler = useCallback(
    (value, opt) => {
      // const findData = formattedOptions.find((row) => row.id === value)
      if (typeof onSelect === 'function') {
        onSelect(value, formattedOptions, opt)
      }
    },
    [formattedOptions, onSelect],
  )

  const dropdownVisibleChangeHandler = useCallback((visible) => {
    setOpen(visible)
  }, [])

  const filterOptionHandler = useCallback((input, option) => {
    if (option.name) {
      return option.name.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0
    }
    return false
  }, [])

  const getPopupContainer = useCallback(() => document.getElementById(containerId.current), [])

  if (!options.sales_person && !isPos) {
    return <></>
  }

  return (
    <div id={containerId.current}>
      <Form.Item
        label={!hideLabel ? label || term.sales_person : null}
        name={props.name ?? 'sales_id'}
        rules={rules}
        normalize={normalize}
        tooltip={tooltip}
      >
        <Select
          ref={innerRef}
          showSearch
          mode={mode === 'multiple' ? 'multiple' : undefined}
          placeholder={formatMessage(
            { id: 'salesPerson.select_sales_person_with_title' },
            {
              sales_person: term.sales_person,
            },
          )}
          disabled={disabled}
          onSelect={selectedHandler}
          loading={isLoading}
          onOpenChange={dropdownVisibleChangeHandler}
          allowClear
          tagRender={
            hideTagOnFirstLoading && (!loadedInit || isLoading || !enabledQuery)
              ? () => null
              : undefined
          }
          options={formattedOptions}
          fieldNames={{ label: 'name', value: 'id' }}
          filterOption={filterOptionHandler}
          getPopupContainer={getPopupContainer}
          {...props}
        />
      </Form.Item>
    </div>
  )
}

export default React.forwardRef((props, ref) => <UserSuggestion innerRef={ref} {...props} />)
